import Icon from '@ant-design/icons';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Row } from 'components/Containers/Row/Row';
import { SectionContainer } from 'components/Containers/SectionContainer/SectionContainer';
import Label from 'components/form/Label/Label';
import { Link } from 'components/navigation/Link/Link';
import PIcon from 'components/personalization/PIcon';
import { buildPlanFromConfiguration } from 'components/premium/helpers/premium.helpers';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { getSimplifiedDate } from 'helpers/dateHelper';
import { get } from 'helpers/objectHelpers';
import { getViewModuleTrackingAttributes } from 'helpers/tracking.helpers';
import { isPlus, isPremium, isPulse } from 'helpers/userHelper';
import { useCMAlerts } from 'hook/cmAlerts.hook';
import { usePremiumAlerts } from 'hook/csidAlerts.hook';
import { useEventHandler } from 'hook/event.hook';
import { useFlag } from 'hook/flag.hook';
import { useSeenModulePixel, useViewModule } from 'hook/helper.hook';
import { useRentPaymentReportHelpers } from 'hook/rentPaymentReport.hook';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { ReactComponent as LiveSupportFilledDashboard } from 'style/_assets/icons/24_7_LiveExperts.svg';
import { ReactComponent as AlertsOutline } from 'style/_assets/icons/alerts-outline.svg';
import { ReactComponent as CreditReportOutline } from 'style/_assets/icons/credit-report-outline.svg';
import { ReactComponent as ActionPlanIcon } from 'style/_assets/icons/gamePlanIcon.svg';
import { ReactComponent as IDTheftInsurance } from 'style/_assets/icons/id-theft-insurance.svg';
import { ReactComponent as RentReportingIcon } from 'style/_assets/icons/rent-reporting-icon.svg';
import { ReactComponent as SecurityShield } from 'style/_assets/icons/securityShield.svg';
import { PREMIUM_FEATURES } from 'types/morph.pagePosition.constants';
import { SUBSCRIPTION_VERTICAL } from 'types/mrph.vertical.constants';
import './PremiumFeatures.scss';
import RentReportingFeature from './components/RentReportingFeature';

const isPremiumQualified = (
  premiumFeature,
  isPremiumUser,
  planNavName,
  planMetadata,
) => {
  if (isPremiumUser && planMetadata) {
    return planMetadata?.hasFeature(premiumFeature);
  }
  return false;
};

const CreateFeature = props => {
  const {
    premiumImage,
    text,
    subText,
    linkTo,
    phoneNumber,
    factor,
    premiumFeature,
    isPremiumUser,
    planNavName,
    planMetadata,
  } = props;
  const eventHandler = useEventHandler();

  const handleLinkRedirect = () => {
    eventHandler(CLICK, {
      'Click Type': 'Navigation',
      'Nav Location': factor,
      'Page Position': 'Premium Features',
      Vertical: SUBSCRIPTION_VERTICAL,
    });
  };
  if (
    isPremiumQualified(
      premiumFeature,
      isPremiumUser,
      planNavName,
      planMetadata,
      props,
    )
  ) {
    return (
      <Link
        to={linkTo}
        className="creditreportpagelink-container margL8"
        onClickCallback={handleLinkRedirect}
      >
        <div className=" premiumFeatureBox  padT10 border-rad8px">
          <div
            className="premiumFeatureLink padB10"
            data-testid={text ? `${text}` : null}
          >
            <div className="premiumFeatureLink-left padL4 padR4">
              <div
                className="premiumFeatureLink-icon"
                data-testid={text ? `${text}-icon` : null}
              >
                <Icon className="menuButton outline" component={premiumImage} />
              </div>
            </div>
            <FlexContainer className="mCaption Caption textInfo">
              <div className="premiumFeatureLink-description">
                <Label
                  type="Subtitle-1"
                  data-testid={text ? `${text}-description` : null}
                >
                  {text}
                </Label>
              </div>

              <div className={phoneNumber ? 'phoneNumber' : ''}>{subText}</div>
              <div
                className="premiumFeatureLink-right padR16"
                data-testid={text ? `${text}-back` : null}
              ></div>
            </FlexContainer>
            <PIcon
              icon={'right_pointing_arrow_icon'}
              className="arrowRightStyle"
            />
          </div>
        </div>
      </Link>
    );
  } else {
    return <></>;
  }
};

export default function PremiumFeatures(props) {
  const isPremiumUser = isPremium(props.userInfo);
  const isPulseUser = isPulse(props.userInfo);
  const isPlusUser = isPlus(props.userInfo);
  const premiumDataConfig = useFlag('premium_data');
  const serviceId = props?.userInfo?.premiumPlanInfo?.serviceId;
  const planMetadata = buildPlanFromConfiguration(
    premiumDataConfig?.config?.all_plans_metadata,
    serviceId,
  );

  const planNavName = isPremiumUser
    ? props.userInfo.premiumPlanInfo.uiDisplayName
    : null;

  const title = isPlusUser
    ? props.userInfo.premiumPlanInfo.uiDisplayName
    : 'Premium';

  const phoneNumber = 'Call 1-855-799-9111';
  const creditReportUpdatedDate = getSimplifiedDate(
    get(props.creditInfo, ['reportPulledDate']),
  );

  const history = useHistory();
  const { cmAlerts } = useCMAlerts();
  const { csidAlerts } = usePremiumAlerts();
  const rentPaymentReport = useRentPaymentReportHelpers();

  const isThereAProvider = cmAlerts?.items[0]?.provider;
  const displayDateCreditMonitorring = getSimplifiedDate(
    cmAlerts?.items[0]?.createDate,
  );
  const mostRecentDateForCreditMonitoring = isThereAProvider
    ? `Last Update  ${displayDateCreditMonitorring}`
    : 'No Updates';
  const isThereAIdProtection = csidAlerts?.items[0];
  const displayDateIdProtection = getSimplifiedDate(
    csidAlerts?.items[0]?.createDate,
  );

  const mostRecentDateForIdProtection = isThereAIdProtection
    ? `Last Update  ${displayDateIdProtection}`
    : 'No Updates';

  const atr = getViewModuleTrackingAttributes({
    pagePosition: PREMIUM_FEATURES,
    modulePosition: props.modulePosition,
    vertical: 'Subscription',
  });
  useViewModule(atr);
  const seenPixel = useSeenModulePixel(atr);

  return (
    <>
      {seenPixel}
      <SectionContainer className={'margT48 m-margT0 nonMobileHide'}>
        <Row className="flex-container flex-mobile-col">
          <div className="mSubtitle-1 fontGray200">{title} Features</div>
          <Row className="flex-container d-flex-column m-flex-column">
            <RentReportingFeature
              text={'Rent Reporting'}
              premiumImage={RentReportingIcon}
              factor="Rent reporting dashboard"
              premiumFeature={'rentReporting'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              isPulseUser={isPulseUser}
              rentPaymentReport={rentPaymentReport}
              isPremiumQualified={isPremiumQualified}
              userInfo={props?.userInfo}
              history={history}
              planMetadata={planMetadata}
            />
            <CreateFeature
              text={'Credit Reports'}
              premiumImage={CreditReportOutline}
              subText={`Last viewed report  ${creditReportUpdatedDate}`}
              linkTo={'/creditReports/view'}
              factor="Credit Report"
              premiumFeature={'premiumCreditReports'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              planMetadata={planMetadata}
            />
            <CreateFeature
              text={'Credit Monitoring'}
              premiumImage={AlertsOutline}
              subText={mostRecentDateForCreditMonitoring}
              linkTo={'/alerts/creditMonitoring'}
              factor="Credit Monitoring"
              premiumFeature={'premiumAdvancedMonitoring'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              planMetadata={planMetadata}
            />
            <CreateFeature
              text={'Action Plan'}
              premiumImage={ActionPlanIcon}
              linkTo={'/actionPlan'}
              factor="Action Plan"
              premiumFeature={'premiumActionPlan'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              planMetadata={planMetadata}
            />
            <CreateFeature
              text={'ID Protection'}
              premiumImage={SecurityShield}
              subText={mostRecentDateForIdProtection}
              linkTo={'/alerts/idProtection'}
              factor="ID Protection"
              premiumFeature={'premiumIdMonitoring'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              planMetadata={planMetadata}
            />
            <CreateFeature
              text={'Live Experts'}
              premiumImage={LiveSupportFilledDashboard}
              subText={phoneNumber}
              linkTo={'/liveExperts'}
              phoneNumber={true}
              factor="Live Experts"
              premiumFeature={'premiumLiveSupport'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              planMetadata={planMetadata}
            />
            <CreateFeature
              text={'$1M Identify Theft Insurance'}
              premiumImage={IDTheftInsurance}
              linkTo={'/liveExperts?tab=identityTheftInsurance'}
              phoneNumber={true}
              factor="$1M Identify Theft Insurance"
              premiumFeature={'premiumIdTheftProtection'}
              isPremiumUser={isPremiumUser}
              planNavName={planNavName}
              planMetadata={planMetadata}
            />
          </Row>
        </Row>
      </SectionContainer>
    </>
  );
}
